import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AttachmentModel } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

const watchEntityKeys = ['agencies', 'attachments']

type AttachmentFilters = {
  attachable_id?: number,
  attachable_type?: string,
  attachment_type?: string,
}

type UseAttachmentsOptions = {
  entityKey?: string,
  filters?: AttachmentFilters,
  performHttpRequests?: boolean,
  sortKey?: string,
}

function useAttachments(options: UseAttachmentsOptions = {}) {
  const { entityKey, filters = {} } = options

  const {
    attachable_id: filterAttachableId,
    attachable_type: filterAttachableType,
    attachment_type: filterAttachmentType,
  } = filters

  const {
    updatedEntities: { agencies: agenciesUpdatedAt, attachments: attachmentsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { attachments } = useSelector(reduxState => reduxState.entities)

  const filteredAttachments = useMemo((): AttachmentModel[] => {
    const filtered = Object.values(attachments).filter((attachment) => {
      const { attachable_id, attachable_type, attachment_type } = attachment

      const matchAttachableId = matchFilterNumber(attachable_id, filterAttachableId)
      const matchAttachableType = filterAttachableType ? attachable_type === filterAttachableType : true
      const matchAttachmentType = filterAttachmentType ? attachment_type === filterAttachmentType : true

      return matchAttachableId && matchAttachableType && matchAttachmentType
    })

    return sortArrayBy(filtered, 'asc', 'id')
  }, [agenciesUpdatedAt, attachmentsUpdatedAt, JSON.stringify(filters)])

  const filteredAttachmentsCount = filteredAttachments.length
  const hasFilteredAttachments = !!filteredAttachmentsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAttachmentsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAttachments } = useReduxAction(
    'attachments',
    'loadAttachments',
    {
      entityKey,
      ...defaultRequestOptions.attachment,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAttachments,
    filteredAttachmentsCount,
    hasFilteredAttachments,
    loading: loadingAttachments,
  }
}

export default useAttachments
